/* eslint-disable no-empty */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { S } from "@/pages/ibloom/styles";

const scriptPromises = {};
const loadScript = (src) => {
  if (!scriptPromises[src]) {
    scriptPromises[src] = new Promise((res, rej) => {
      const s = document.createElement("script");
      s.src = src; s.onload = res;
      s.onerror = (e) => { delete scriptPromises[src]; rej(e); };
      document.head.appendChild(s);
    });
  }
  return scriptPromises[src];
};

const IGNORED = ["person", "tv", "laptop", "chair", "couch", "bed", "dining table", "keyboard", "mouse", "refrigerator"];
const COOLDOWN_MS = 20000;   // CCTV pace — one product at a time, slowly
const LOCK_MS = 3000;        // target must stay stable this long before capture
const FRAME_MS = 500;        // ~2 fps detection, like a CCTV feed

const iou = (a, b) => {
  const ix = Math.max(0, Math.min(a[0] + a[2], b[0] + b[2]) - Math.max(a[0], b[0]));
  const iy = Math.max(0, Math.min(a[1] + a[3], b[1] + b[3]) - Math.max(a[1], b[1]));
  const inter = ix * iy;
  if (inter <= 0) return 0;
  return inter / (a[2] * a[3] + b[2] * b[3] - inter);
};

export default function ShelfAuto() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const modelRef = useRef(null);
  const runningRef = useRef(false);
  const cooldownRef = useRef(false);
  const wakeLockRef = useRef(null);
  const sessionRef = useRef(0);
  const lockRef = useRef(null); // {bbox, since} — stability tracker before Gemini handoff

  const [phase, setPhase] = useState("loading"); // loading | live | error
  const [targets, setTargets] = useState(0);
  const [cooldown, setCooldown] = useState(false);
  const [feed, setFeed] = useState([]); // {key, img, title, price, status}
  const [source, setSource] = useState("phone"); // "phone" | "rtsp"

  useEffect(() => {
    if (authLoading) return;
    if (!user) { toast("Sign in to use Ai CCTV upload."); navigate("/login"); return; }
    if (source !== "phone") { stopAll(); return; }
    setPhase("loading");
    const sid = ++sessionRef.current;
    start(sid);
    return () => { sessionRef.current += 1; stopAll(); };
  }, [authLoading, user, source]); // eslint-disable-line react-hooks/exhaustive-deps

  const stopAll = () => {
    runningRef.current = false;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    try { wakeLockRef.current?.release(); } catch {}
  };

  const start = async (sid) => {
    try {
      await loadScript("https://cdn.jsdelivr.net/npm/@tensorflow/tfjs");
      await loadScript("https://cdn.jsdelivr.net/npm/@tensorflow-models/coco-ssd");
      if (sessionRef.current !== sid) return;
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment", width: { ideal: 1280 } }, audio: false });
      if (sessionRef.current !== sid) { stream.getTracks().forEach((t) => t.stop()); return; }
      streamRef.current = stream;
      try { wakeLockRef.current = await navigator.wakeLock?.request("screen"); } catch {}
      if (!modelRef.current) modelRef.current = await window.cocoSsd.load();
      if (sessionRef.current !== sid) return;
      const v = videoRef.current;
      v.srcObject = stream;
      await v.play().catch(() => {});
      setPhase("live");
      runningRef.current = true;
      setTimeout(loop, FRAME_MS);
    } catch (e) {
      console.error("ShelfAuto start failed:", e);
      if (sessionRef.current === sid) setPhase("error");
    }
  };

  const loop = async () => {
    if (!runningRef.current) return;
    const v = videoRef.current, c = canvasRef.current;
    if (!v || !c || !v.videoWidth || !modelRef.current) { setTimeout(loop, FRAME_MS); return; }
    const rect = c.parentElement.getBoundingClientRect();
    c.width = rect.width; c.height = rect.height;
    let preds = [];
    try { preds = await modelRef.current.detect(v); } catch {}
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);

    // object-fit: cover mapping video → canvas
    const vr = v.videoWidth / v.videoHeight, cr = c.width / c.height;
    let rw, rh, ox = 0, oy = 0;
    if (cr > vr) { rw = c.width; rh = rw / vr; oy = (c.height - rh) / 2; }
    else { rh = c.height; rw = rh * vr; ox = (c.width - rw) / 2; }
    const sx = rw / v.videoWidth, sy = rh / v.videoHeight;

    const cx = v.videoWidth / 2, cy = v.videoHeight / 2;
    const maxDist = Math.sqrt(cx * cx + cy * cy);
    let valid = preds.filter((p) => !IGNORED.includes(p.class) && p.score > 0.3).map((p) => {
      const [x, y, w, h] = p.bbox;
      const dist = Math.sqrt((cx - (x + w / 2)) ** 2 + (cy - (y + h / 2)) ** 2);
      const centerScore = Math.max(0, 1 - dist / maxDist);
      const areaScore = (w * h) / (v.videoWidth * v.videoHeight);
      return { ...p, centerScore, areaScore, prominence: p.score * 0.2 + centerScore * 0.6 + areaScore * 0.2 };
    }).sort((a, b) => b.prominence - a.prominence);

    setTargets(valid.length);

    // --- CCTV lock-on: the main product must stay stable for LOCK_MS before Gemini ---
    const now = Date.now();
    let lockPct = 0;
    const best = valid[0];
    if (best && !cooldownRef.current && ((best.centerScore > 0.6 && best.areaScore > 0.06) || best.prominence > 0.5)) {
      if (lockRef.current && iou(lockRef.current.bbox, best.bbox) > 0.45) {
        lockPct = Math.min(1, (now - lockRef.current.since) / LOCK_MS);
        lockRef.current.bbox = best.bbox;
      } else {
        lockRef.current = { bbox: best.bbox, since: now };
      }
    } else {
      lockRef.current = null;
    }

    // Draw: secondary boxes first, then circle the main product
    valid.forEach((p, i) => {
      const [x, y, w, h] = p.bbox;
      const X = x * sx + ox, Y = y * sy + oy, W = w * sx, H = h * sy;
      if (i === 0) {
        // Circle (ellipse) around the locked-on product, CCTV style
        ctx.strokeStyle = lockPct >= 1 ? "#5EE39A" : "#FFD700";
        ctx.lineWidth = 3;
        ctx.setLineDash(lockPct >= 1 ? [] : [8, 6]);
        ctx.beginPath();
        ctx.ellipse(X + W / 2, Y + H / 2, W / 2 + 10, H / 2 + 10, 0, 0, Math.PI * 2);
        ctx.stroke();
        ctx.setLineDash([]);
        const label = lockPct >= 1 ? "✓ PRODUCT" : `PRODUCT · locking ${Math.round(lockPct * 100)}%`;
        ctx.font = "bold 11px Inter, sans-serif";
        const tw = ctx.measureText(label).width;
        ctx.fillStyle = lockPct >= 1 ? "#5EE39A" : "#FFD700";
        ctx.fillRect(X + W / 2 - tw / 2 - 6, Y - 26, tw + 12, 18);
        ctx.fillStyle = "#000";
        ctx.fillText(label, X + W / 2 - tw / 2, Y - 13);
      } else {
        ctx.strokeStyle = "#22c55e";
        ctx.lineWidth = 1.5;
        ctx.strokeRect(X, Y, W, H);
        ctx.font = "bold 10px Inter, sans-serif";
        const tw = ctx.measureText("PRODUCT").width;
        ctx.fillStyle = "#22c55e";
        ctx.fillRect(X, Y - 16, tw + 8, 16);
        ctx.fillStyle = "#000";
        ctx.fillText("PRODUCT", X + 4, Y - 4);
      }
    });

    if (lockPct >= 1 && best && !cooldownRef.current) {
      lockRef.current = null;
      autoCapture(best);
    }
    setTimeout(loop, FRAME_MS);
  };

  const autoCapture = (pred) => {
    cooldownRef.current = true;
    setCooldown(true);
    setTimeout(() => { cooldownRef.current = false; setCooldown(false); }, COOLDOWN_MS);
    const v = videoRef.current;
    const [x, y, w, h] = pred.bbox;
    const px = w * 0.35, py = h * 0.35;
    const cx0 = Math.max(0, x - px), cy0 = Math.max(0, y - py);
    const cw = Math.min(v.videoWidth - cx0, w + px * 2), ch = Math.min(v.videoHeight - cy0, h + py * 2);
    const t = document.createElement("canvas");
    t.width = cw; t.height = ch;
    t.getContext("2d").drawImage(v, cx0, cy0, cw, ch, 0, 0, cw, ch);
    const dataUrl = t.toDataURL("image/jpeg", 0.85);
    const key = Date.now();
    setFeed((f) => [{ key, img: dataUrl, title: "Identifying product…", price: null, status: "listing" }, ...f].slice(0, 25));
    api.post("/shelf/auto/list", { image_data: dataUrl })
      .then((r) => {
        const d = r.data;
        if (d.duplicate) {
          setFeed((f) => f.map((it) => (it.key === key ? { ...it, title: d.title, status: "duplicate" } : it)));
        } else {
          setFeed((f) => f.map((it) => (it.key === key ? { ...it, title: d.title, price: d.price, status: "live" } : it)));
          toast(`🛰️ Auto-listed: ${d.title.slice(0, 40)} @ ₹${d.price}`);
        }
      })
      .catch((e) => {
        setFeed((f) => f.map((it) => (it.key === key ? { ...it, title: e?.response?.data?.detail || "Couldn't identify — try again", status: "failed" } : it)));
      });
  };

  return (
    <div data-testid="shelf-auto-page" style={{ minHeight: "100dvh", background: "#0E1512", color: "white" }}>
      <div style={{ position: "sticky", top: 0, zIndex: 20, background: "rgba(14,21,18,0.92)", backdropFilter: "blur(10px)", borderBottom: "1px solid #1F2B25", padding: "12px 16px", display: "flex", alignItems: "center", gap: 12 }}>
        <button data-testid="shelf-auto-back-btn" onClick={() => { stopAll(); navigate(-1); }} style={{ width: 34, height: 34, borderRadius: "50%", border: "1px solid #2A3A32", background: "#16211C", fontSize: 16, cursor: "pointer", color: "#9FE8C4", flexShrink: 0 }}>‹</button>
        <div style={{ ...S.fraunces, fontSize: 18, fontWeight: 800, color: "#FFD700" }} data-no-translate>📹 Ai CCTV upload</div>
        <span style={{ fontSize: 9, fontWeight: 900, letterSpacing: 0.8, background: "#C97B3C", color: "white", borderRadius: 100, padding: "3px 8px" }} data-no-translate>BETA</span>
        {phase === "live" && (
          <span data-testid="shelf-auto-targets" style={{ marginLeft: "auto", fontSize: 10.5, fontWeight: 800, color: targets > 0 ? "#5EE39A" : "#6B8578", background: "#131E19", border: "1px solid #1F2B25", borderRadius: 100, padding: "4px 10px" }} data-no-translate>
            {targets} target{targets === 1 ? "" : "s"}
          </span>
        )}
      </div>

      <div style={{ maxWidth: 560, margin: "0 auto", padding: "14px 16px 60px" }}>
        <div data-testid="shelf-auto-source-toggle" style={{ display: "flex", gap: 3, padding: 3, background: "#131E19", border: "1px solid #1F2B25", borderRadius: 100, marginBottom: 12 }}>
          {[
            { k: "phone", label: "📱 Phone camera" },
            { k: "rtsp", label: "🎥 CCTV camera (RTSP)" },
          ].map((s) => (
            <button key={s.k} data-testid={`shelf-auto-source-${s.k}`} onClick={() => setSource(s.k)}
              style={{ flex: 1, padding: "9px 6px", borderRadius: 100, border: "none", cursor: "pointer", fontSize: 11.5, fontWeight: 800, background: source === s.k ? "#1F4A3A" : "transparent", color: source === s.k ? "#9FE8C4" : "#6B8578", whiteSpace: "nowrap" }} data-no-translate>
              {s.label}
            </button>
          ))}
        </div>

        {source === "rtsp" && <RtspPanel />}

        {source === "phone" && (
        <>
        <div style={{ position: "relative", borderRadius: 20, overflow: "hidden", border: "2px solid #1F4A3A", background: "black", height: "42vh" }}>
          <video ref={videoRef} data-testid="shelf-auto-video" muted playsInline style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} />
          {phase === "loading" && (
            <div style={{ position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, background: "rgba(0,0,0,0.7)" }}>
              <div style={{ fontSize: 30 }}>🧠</div>
              <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: "#9FE8C4" }} data-no-translate>LOADING EDGE AI MODEL…</div>
            </div>
          )}
          {phase === "error" && (
            <div data-testid="shelf-auto-error" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, textAlign: "center", fontSize: 13, color: "#FF9A8A", fontWeight: 700 }}>
              Camera or AI model failed to load. Allow camera permission and reload.
            </div>
          )}
          {cooldown && (
            <div data-testid="shelf-auto-cooldown" style={{ position: "absolute", left: 10, bottom: 10, background: "rgba(0,0,0,0.7)", borderRadius: 100, padding: "6px 12px", fontSize: 12, fontWeight: 800, color: "#FFD700" }}>
              ⏳ Product locked — listing… next scan in 20s
            </div>
          )}
        </div>

        <div style={{ marginTop: 10, background: "#131E19", border: "1px solid #1F2B25", borderRadius: 14, padding: "10px 14px", fontSize: 12, color: "#A8BDB2", lineHeight: 1.5 }}>
          📹 <b style={{ color: "#FFD700" }}>CCTV mode (BETA):</b> point the camera at your shelf and leave it. The edge AI watches slowly like a CCTV camera, circles each <b style={{ color: "#FFD700" }}>PRODUCT</b>, locks on for a few seconds to confirm — and only then MunimJi identifies it, gives an approx ₹ price and <b style={{ color: "#5EE39A" }}>lists it on Mapbazar</b>, one product at a time. Edit prices later in My Shop.
        </div>

        <div data-testid="shelf-auto-feed" style={{ marginTop: 16, display: "flex", flexDirection: "column", gap: 10 }}>
          {feed.map((it) => (
            <div key={it.key} data-testid={`shelf-auto-item-${it.key}`} style={{ display: "flex", gap: 10, alignItems: "center", background: "#131E19", border: "1px solid #2A3A32", borderRadius: 14, padding: 10 }}>
              <img src={it.img} alt="" style={{ width: 58, height: 58, objectFit: "contain", borderRadius: 10, background: "#0E1512", flexShrink: 0 }} />
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, fontWeight: 800, color: "white", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title}</div>
                <div style={{ marginTop: 4, fontSize: 11, fontWeight: 800 }} data-no-translate>
                  {it.status === "listing" && <span style={{ color: "#9FE8C4" }}>✨ MunimJi writing listing…</span>}
                  {it.status === "live" && <span style={{ color: "#5EE39A" }}>● LIVE on Mapbazar · ₹{it.price} approx</span>}
                  {it.status === "duplicate" && <span style={{ color: "#E8C46B" }}>✓ Already in your catalogue — restocked</span>}
                  {it.status === "failed" && <span style={{ color: "#FF9A8A" }}>✕ {""}Failed</span>}
                </div>
              </div>
            </div>
          ))}
        </div>
        </>
        )}
      </div>
    </div>
  );
}

const RTSP_INTERVALS = [5, 15, 30, 60];

function RtspPanel() {
  const [cam, setCam] = useState(null);
  const [running, setRunning] = useState(false);
  const [scans, setScans] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [editing, setEditing] = useState(false);
  const [url, setUrl] = useState("");
  const [intervalMin, setIntervalMin] = useState(15);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [err, setErr] = useState("");
  const [canForce, setCanForce] = useState(false);

  const load = () => api.get("/shelf/cctv/status").then((r) => {
    setCam(r.data.camera); setRunning(r.data.running); setScans(r.data.scans || []); setLoaded(true);
  }).catch(() => setLoaded(true));

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, []);

  const connect = async (force = false) => {
    setBusy(true); setErr("");
    try {
      const r = await api.post("/shelf/cctv/connect", { rtsp_url: url.trim(), interval_minutes: intervalMin, force }, { timeout: 60000 });
      setCanForce(false); setEditing(false);
      toast(r.data.connected ? "🎥 Camera connected — AI is watching your shelf now" : "💾 Saved — we'll keep retrying your camera automatically");
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Couldn't connect. Try again.";
      setErr(msg);
      setCanForce(true);
    } finally { setBusy(false); }
  };

  const captureNow = async () => {
    setChecking(true);
    try {
      const r = await api.post("/shelf/cctv/capture-now", {}, { timeout: 120000 });
      const d = r.data;
      if (d.ok) toast(`📸 Frame checked: ${d.detected} product(s) seen · ${d.matched} restocked · ${(d.listed || []).length} newly listed`);
      else toast(d.error || "Camera unreachable");
      load();
    } catch (e) { toast(e?.response?.data?.detail || "Check failed — try again"); }
    finally { setChecking(false); }
  };

  const disconnect = async () => {
    try { await api.post("/shelf/cctv/stop"); toast("⏸ CCTV watcher stopped"); load(); } catch { toast("Couldn't stop — try again"); }
  };

  const card = { background: "#131E19", border: "1px solid #1F2B25", borderRadius: 16, padding: 14 };
  const showForm = !cam || editing;

  if (!loaded) return <div style={{ ...card, textAlign: "center", color: "#6B8578", fontSize: 12.5 }}>Loading camera status…</div>;

  return (
    <div data-testid="rtsp-panel" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      {showForm ? (
        <div style={card}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#FFD700", marginBottom: 4 }} data-no-translate>🎥 Connect your shop CCTV</div>
          <div style={{ fontSize: 11.5, color: "#A8BDB2", lineHeight: 1.55, marginBottom: 12 }}>
            Paste your camera's <b style={{ color: "#9FE8C4" }}>RTSP URL</b>. Our server grabs a frame every few minutes, AI spots the products and <b style={{ color: "#5EE39A" }}>lists them on Mapbazar automatically</b> — even when this page is closed.
          </div>
          <input
            data-testid="rtsp-url-input"
            value={url}
            onChange={(e) => { setUrl(e.target.value); setErr(""); setCanForce(false); }}
            placeholder="rtsp://user:pass@your-ddns.com:554/stream"
            autoCapitalize="none" autoCorrect="off" spellCheck={false}
            style={{ width: "100%", boxSizing: "border-box", padding: "12px 13px", borderRadius: 12, border: err ? "1.5px solid #B4452A" : "1px solid #2A3A32", background: "#0E1512", color: "#D9EDE3", fontSize: 12.5, outline: "none", fontFamily: "monospace" }}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 10 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#6B8578", flexShrink: 0 }}>AI checks every</span>
            <div style={{ display: "flex", gap: 5, flex: 1 }} data-no-translate>
              {RTSP_INTERVALS.map((m) => (
                <button key={m} data-testid={`rtsp-interval-${m}`} onClick={() => setIntervalMin(m)}
                  style={{ flex: 1, padding: "7px 4px", borderRadius: 100, border: intervalMin === m ? "none" : "1px solid #2A3A32", background: intervalMin === m ? "#1F4A3A" : "transparent", color: intervalMin === m ? "#9FE8C4" : "#6B8578", fontSize: 11, fontWeight: 800, cursor: "pointer" }}>
                  {m < 60 ? `${m}m` : "1h"}
                </button>
              ))}
            </div>
          </div>
          {err && <div data-testid="rtsp-error" style={{ marginTop: 10, fontSize: 11.5, color: "#FF9A8A", lineHeight: 1.5, background: "#1D1512", border: "1px solid #4A2A1F", borderRadius: 10, padding: "8px 11px" }}>⚠ {err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button data-testid="rtsp-connect-btn" onClick={() => connect(false)} disabled={busy || !url.trim()}
              style={{ flex: 1, padding: 13, borderRadius: 100, border: "none", background: busy || !url.trim() ? "#2A3A32" : "linear-gradient(135deg, #C97B3C, #8B1A4A)", color: "white", fontWeight: 800, fontSize: 13, cursor: busy || !url.trim() ? "not-allowed" : "pointer" }}>
              {busy ? "Connecting… (up to 40s)" : "🎥 Connect camera"}
            </button>
            {editing && (
              <button data-testid="rtsp-cancel-edit" onClick={() => { setEditing(false); setErr(""); setCanForce(false); }}
                style={{ padding: "13px 16px", borderRadius: 100, border: "1px solid #2A3A32", background: "transparent", color: "#6B8578", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>Cancel</button>
            )}
          </div>
          {canForce && (
            <button data-testid="rtsp-force-btn" onClick={() => connect(true)} disabled={busy}
              style={{ width: "100%", marginTop: 8, padding: 11, borderRadius: 100, border: "1px solid #4A3A1F", background: "transparent", color: "#E8C46B", fontWeight: 700, fontSize: 12, cursor: "pointer" }}>
              💾 Save anyway — keep retrying every {intervalMin < 60 ? `${intervalMin} min` : "hour"}
            </button>
          )}
          <div style={{ marginTop: 12, fontSize: 10.5, color: "#6B8578", lineHeight: 1.55 }}>
            💡 On CP Plus / Hikvision / Dahua DVRs, enable port-forwarding or DDNS so the URL works from the internet. Typical format: <span data-no-translate style={{ fontFamily: "monospace", color: "#9FE8C4" }}>rtsp://admin:pass@shopcam.ddns.net:554/cam/realmonitor?channel=1&subtype=0</span>
          </div>
        </div>
      ) : (
        <div style={card} data-testid="rtsp-status-card">
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <span data-testid="rtsp-live-badge" style={{ fontSize: 10, fontWeight: 900, letterSpacing: 0.8, padding: "4px 10px", borderRadius: 100, background: cam.active && running && !cam.last_error ? "#0F3D2E" : "#3A2A1A", color: cam.active && running && !cam.last_error ? "#5EE39A" : "#E8C46B" }} data-no-translate>
              {cam.active && running ? (cam.last_error ? "◌ RETRYING" : "● WATCHING") : "⏸ STOPPED"}
            </span>
            <span style={{ fontSize: 10.5, color: "#6B8578", fontWeight: 700 }} data-no-translate>every {cam.interval_minutes < 60 ? `${cam.interval_minutes}m` : "1h"}</span>
            <button data-testid="rtsp-edit-btn" onClick={() => { setUrl(""); setIntervalMin(cam.interval_minutes || 15); setEditing(true); }}
              style={{ marginLeft: "auto", padding: "5px 12px", borderRadius: 100, border: "1px solid #2A3A32", background: "transparent", color: "#9FE8C4", fontSize: 10.5, fontWeight: 800, cursor: "pointer" }}>✏️ Edit</button>
          </div>

          {cam.last_frame ? (
            <img data-testid="rtsp-last-frame" src={cam.last_frame} alt="Last CCTV frame" style={{ width: "100%", borderRadius: 12, border: "1px solid #2A3A32", display: "block" }} />
          ) : (
            <div style={{ height: 120, borderRadius: 12, border: "1px dashed #2A3A32", display: "flex", alignItems: "center", justifyContent: "center", color: "#6B8578", fontSize: 12 }}>No frame yet — waiting for the camera…</div>
          )}

          <div data-no-translate style={{ marginTop: 8, fontSize: 10.5, color: "#6B8578", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{cam.rtsp_url_masked}</div>

          {cam.last_error && <div data-testid="rtsp-status-error" style={{ marginTop: 8, fontSize: 11.5, color: "#FF9A8A", lineHeight: 1.5 }}>⚠ {cam.last_error}</div>}

          <div style={{ display: "flex", gap: 8, marginTop: 10, textAlign: "center" }} data-no-translate>
            {[
              [cam.last_detected ?? "—", "seen last check"],
              [cam.last_new ?? "—", "newly listed"],
              [cam.total_listed || 0, "total listed"],
            ].map(([v, l], i) => (
              <div key={i} style={{ flex: 1, background: "#0E1512", border: "1px solid #1F2B25", borderRadius: 12, padding: "8px 4px" }}>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#9FE8C4" }}>{v}</div>
                <div style={{ fontSize: 9, color: "#6B8578", fontWeight: 700 }}>{l}</div>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
            <button data-testid="rtsp-capture-now-btn" onClick={captureNow} disabled={checking}
              style={{ flex: 1.4, padding: 12, borderRadius: 100, border: "none", background: checking ? "#2A3A32" : "#1F4A3A", color: "#9FE8C4", fontWeight: 800, fontSize: 12.5, cursor: checking ? "wait" : "pointer" }}>
              {checking ? "📸 Checking shelf…" : "📸 Check shelf now"}
            </button>
            {cam.active ? (
              <button data-testid="rtsp-stop-btn" onClick={disconnect}
                style={{ flex: 1, padding: 12, borderRadius: 100, border: "1px solid #4A2A1F", background: "transparent", color: "#FF9A8A", fontWeight: 800, fontSize: 12.5, cursor: "pointer" }}>⏸ Stop</button>
            ) : (
              <button data-testid="rtsp-resume-btn" onClick={() => { setUrl(""); setEditing(true); }}
                style={{ flex: 1, padding: 12, borderRadius: 100, border: "1px solid #2A3A32", background: "transparent", color: "#9FE8C4", fontWeight: 800, fontSize: 12.5, cursor: "pointer" }}>▶ Reconnect</button>
            )}
          </div>

          {cam.last_capture_at && <div style={{ marginTop: 8, fontSize: 10.5, color: "#6B8578" }} data-no-translate>Last good frame: {new Date(cam.last_capture_at).toLocaleString("en-IN")}</div>}

          <div style={{ marginTop: 10, fontSize: 11, color: "#A8BDB2", lineHeight: 1.5, background: "#0E1512", border: "1px solid #1F2B25", borderRadius: 10, padding: "8px 11px" }}>
            ☁️ Runs on our servers — <b style={{ color: "#5EE39A" }}>you can close this page</b>. New products go live with an approx price; fix prices in My Shop.
          </div>

          {scans.length > 0 && (
            <div data-testid="rtsp-scan-log" style={{ marginTop: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, color: "#6B8578", marginBottom: 6 }} data-no-translate>RECENT CHECKS</div>
              {scans.map((s) => (
                <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: "1px solid #1F2B25", fontSize: 11, color: "#A8BDB2" }} data-no-translate>
                  <span style={{ color: "#6B8578", flexShrink: 0 }}>{new Date(s.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                  <span>👁 {s.detected_count}</span>
                  <span>✓ {s.matched_count} restocked</span>
                  <span style={{ color: s.new_count > 0 ? "#5EE39A" : "#6B8578", fontWeight: s.new_count > 0 ? 800 : 400 }}>🆕 {s.new_count} listed</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
